import React, {
  CSSProperties,
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import {
  arrow,
  autoPlacement,
  autoUpdate,
  hide,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import { Placement } from "@floating-ui/utils";
import { faClose } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import { stopEventPropagation, stopEvents } from "@mapmycustomers/shared/util/browser";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button } from "@mapmycustomers/ui";

import { TourStepOptions } from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import { TOUR_ACTIVE_ATTRIBUTE_NAME } from "@app/util/consts";
import useInterval from "@app/util/hook/useInterval";

import styles from "./MmcTourTooltip.module.scss";

const messages = defineMessages({
  nextButton: {
    id: "guides.tour.tooltip.buttons.next",
    defaultMessage: "Next",
    description: "Next button title in a Tour's tooltip",
  },
});

const HIGHLIGHT_OFFSET = 8;

// When tooltip has "top" placement, we display an arrow at the bottom of it. And so on.
// This const helps to determine the opposite side.
const PLACEMENT_OPPOSITE_SIDE = {
  bottom: "top",
  left: "right",
  right: "left",
  top: "bottom",
};

export interface MmcTourTooltipProps extends TourStepOptions {
  content?: ReactNode;
  nextButtonDisabled?: boolean;
  nextButtonText?: ReactNode;
  onElementFound?: () => void;
  onHide?: () => void;
  onNextClick: () => void;
  position?: Placement;
  progress?: ReactNode;
  selector?: string;
  selectorFoundDelay?: number;
  title?: ReactNode;
  total?: number;
  visible?: boolean;
}

export const MmcTourTooltip: FunctionComponent<MmcTourTooltipProps> = ({
  content,
  disableInteraction = true,
  nextButtonDisabled,
  nextButtonText,
  onElementFound,
  onHide,
  onNextClick,
  position,
  preventMouseDown = false,
  progress,
  selector,
  selectorFoundDelay = 100, // waiting for 100ms by default
  showBackground = true,
  showButtons = true,
  title,
  visible,
}) => {
  const intl = useIntl();
  const arrowRef = useRef<HTMLDivElement>(null);

  const { elements, floatingStyles, middlewareData, placement, refs, update } = useFloating({
    middleware: [
      ...(position ? [] : [autoPlacement()]),
      offset(6 + HIGHLIGHT_OFFSET),
      shift(),
      hide(),
      arrow({ element: arrowRef.current }),
    ],
    placement: position,
    whileElementsMounted: autoUpdate,
  });

  const refRef = useRef<any>(null);
  useEffect(() => {
    const isElementPresent = () => !selector || !!document.querySelector(selector);

    waitForElement(isElementPresent, () => {
      // console.log(
      //   "!!! element found",
      //   selector,
      //   selector ? document.querySelector(selector) : null
      // );
      if (selector) {
        setTimeout(() => {
          onElementFound?.();
          refs.setReference(document.querySelector(selector));
          refRef.current = document.querySelector(selector)!;
          refRef.current?.scrollIntoView({ behavior: "instant", block: "center" });
        }, selectorFoundDelay ?? 0);
      } else {
        refRef.current = null;
      }
    });
  }, [selector, refs, selectorFoundDelay, onElementFound]);

  const tooltipStyles: CSSProperties = useMemo(() => {
    if (!selector) {
      // position at the center of a screen
      return {
        left: "50%",
        position: "absolute",
        top: "50%",
        transform: "translate(-50%, -50%)",
        visibility: "visible",
      };
    }

    if (!elements.reference) {
      return { visibility: "hidden" };
    }

    const visible =
      !!floatingStyles.transform && !`${floatingStyles.transform}`.includes("0px,0px");
    return { ...floatingStyles, visibility: visible ? "visible" : "hidden" };
  }, [floatingStyles, selector, elements.reference]);

  // we need to force highlightStyles to re-render when necessary
  const [highlightRerender, setHighlightRerender] = useState(0);
  const highlightStyles: CSSProperties = useMemo(
    () => {
      if (selector && !elements.reference) {
        return {
          visibility: "hidden",
        };
      }

      if (!elements.reference || !selector) {
        // effectively hiding the highlight by displaying it at a screen center
        return {
          border: "none",
          // only dark bg and no focus rectangle
          boxShadow: "rgba(0, 0,0 , 0.5) 0px 0px 0px 5000px",
          inset: "50%",
          position: "absolute",
        };
      }
      const rect = elements.reference.getBoundingClientRect();
      return {
        height: `${rect.height + 2 * HIGHLIGHT_OFFSET}px`,
        left: `${rect.left - HIGHLIGHT_OFFSET}px`,
        position: "absolute",
        top: `${rect.top - HIGHLIGHT_OFFSET}px`,
        width: `${rect.width + 2 * HIGHLIGHT_OFFSET}px`,
      };
    },
    // highlightRerender is not used in this memo, but memo should
    // re-run if highlightRerender is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [elements.reference, highlightRerender, selector]
  );

  const [arrowStyles, arrowSide] = useMemo(() => {
    if (!arrowRef.current) {
      return [{}, "none"];
    }

    const arrowData = middlewareData?.arrow;
    const side = placement.split("-")[0] as keyof typeof PLACEMENT_OPPOSITE_SIDE;
    const arrowSide = PLACEMENT_OPPOSITE_SIDE[side]!;

    return [
      {
        left: arrowData?.x != null ? `${arrowData.x}px` : "",
        top: arrowData?.y != null ? `${arrowData.y}px` : "",
        // the "arrowSide" element should go after "left" or "top" to override 'em if necessary
        // eslint-disable-next-line perfectionist/sort-objects
        [arrowSide]: `${-arrowRef.current.offsetWidth}px`,
      },
      arrowSide,
    ];
  }, [placement, middlewareData?.arrow]);

  const currentElement = selector ? document.querySelector(selector) : null;
  const elementChanged = refs.reference.current !== currentElement;
  const [processedSelector, setProcessedSelector] = useState(selector);

  useInterval(
    useDynamicCallback(() => {
      const currentElement = selector ? document.querySelector(selector) : null;
      // don't mark it changed if we simply missed the element (e.g. due to react re-render
      // or if selector is not defined)
      const elementChanged = !!currentElement && refs.reference.current !== currentElement;

      if (elementChanged) {
        onElementFound?.();
        refs.setReference(currentElement);
        currentElement?.scrollIntoView({ behavior: "instant", block: "nearest" });
        setProcessedSelector(selector);
        update();
      }

      setHighlightRerender((x) => x + 1);
    }),
    true,
    selector !== processedSelector ? 100 : 1000
  );

  const tooltipStylesRef = useRef<CSSProperties>({});
  if (!elementChanged || !selector) {
    const previousHidden = tooltipStylesRef.current.visibility === "hidden";
    tooltipStylesRef.current = tooltipStyles;
    if (previousHidden && tooltipStylesRef.current.visibility !== "hidden") {
      tooltipStylesRef.current.transition = "none";
    }
  }

  const referenceHidden =
    !!selector && ((middlewareData?.hide?.escaped ?? false) || !currentElement);

  // console.log("!!! tooltip", selector, elements, floatingStyles, tooltipStyles);

  const [transitionDisabled, setTransitionDisabled] = useState(!selector);
  useEffect(() => {
    if (!selector) {
      setTransitionDisabled(true);
    } else {
      // when transitioning from card w/o selector to a card with selector,
      // we're skipping the animation by delaying setting this flag by
      // some timeout which is enough for all transitions to complete.
      setTimeout(() => setTransitionDisabled(false), 500);
    }
  }, [selector]);

  useEffect(() => {
    if (!disableInteraction) {
      return;
    }
    document.body.setAttribute(TOUR_ACTIVE_ATTRIBUTE_NAME, "");
    return () => {
      document.body.removeAttribute(TOUR_ACTIVE_ATTRIBUTE_NAME);
    };
  }, [disableInteraction]);

  if (!visible || referenceHidden) {
    return null;
  }

  return createPortal(
    <div className={styles.container}>
      {disableInteraction && <div className={styles.backdrop} onClickCapture={stopEvents} />}
      <div
        className={cn(styles.highlight, {
          [styles.interactive]: !disableInteraction,
          [styles.noBackground]: !showBackground,
          [styles.noTransition]: transitionDisabled,
        })}
        onClickCapture={disableInteraction ? stopEvents : undefined}
        style={highlightStyles}
      />
      <div
        className={cn(styles.tooltip, { [styles.invisible]: !tooltipStylesRef.current.transform })}
        // see TourStepOptions.preventMouseDown; touchstart too — rc-trigger closes on both
        onMouseDown={preventMouseDown ? stopEventPropagation : undefined}
        onTouchStart={preventMouseDown ? stopEventPropagation : undefined}
        ref={refs.setFloating}
        style={tooltipStylesRef.current}
      >
        <div
          className={cn(styles.arrow, {
            [styles.hidden]: !selector,
            [styles[arrowSide]]: true,
          })}
          ref={arrowRef}
          style={arrowStyles}
        />
        <div className={styles.header}>
          {title ? <div className={styles.title}>{title}</div> : null}
          <FontAwesomeIcon className={styles.closeIcon} icon={faClose} onClick={onHide} />
        </div>
        <div className={styles.message}>{content}</div>
        <div className={styles.footer}>
          {isDefined(progress) && <div className={styles.progress}>{progress}</div>}
          {showButtons && (
            <Button
              data-action="mmc-tour-next"
              disabled={nextButtonDisabled}
              onClick={onNextClick}
              type="primary"
            >
              {nextButtonText ?? intl.formatMessage(messages.nextButton)}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MmcTourTooltip;
